import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import React from 'react';
import { HideSelectedActions } from '../DataGrid/WSDataGrid';

interface ThreeDotMenuProps {
  items: string[];
  onAction: (action: string) => void;
  align?: 'horizontal' | 'vertical';
  rows?: any;
  value?: any;
  field?: { fieldName: string; ValueCheck: { value: string; actionCheck: any } };
  hideAction?: {
    actionName: {
      fieldName: string;
      values: string[];
      hideAction: string[];
    }[];
  };
  hideSelectedActions?: HideSelectedActions;
}

const ThreeDotMenu: React.FC<ThreeDotMenuProps> = ({
  items,
  onAction,
  align = 'vertical',
  field,
  value,
  hideAction,
  hideSelectedActions,
  rows
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: string) => {
    onAction(action);
    handleClose();
  };

  // Old hideAction check (optional additional logic)
  const checkFields = (actionItem: string): boolean => {
    if (!hideAction || !value || !field) return false;

    return hideAction?.actionName?.some((item) => {
      const hideActionsArray = item.hideAction.flatMap((action) => action.split(','));
      return (
        item.fieldName === field.fieldName &&
        item.values.includes(value) &&
        hideActionsArray.includes(actionItem?.trim())
      );
    });
  };

  // hideSelectedActions helpers
  const shouldHideAction = (row: any, action: string): boolean => {
    return hideSelectedActions?.fieldToHide?.some((item) => {
      return item.hideValue.includes(row[item.field]) && item.hideActions.includes(action);
    }) ?? false;
  };

  const shouldShowAction = (row: any, action: string): boolean => {
    if (!hideSelectedActions?.fieldToShow) return false;

    return hideSelectedActions.fieldToShow.some((item) => {
      return (
        item.showValue.includes(row[item.field]) &&
        item.showActions.includes(action)
      );
    });
  };

const getVisibleActions = (): string[] => {
  if (!rows || !items || items.length === 0) return [];

  const hidden = new Set<string>();
  const whitelist = new Map<string, boolean>(); // action -> whether explicitly allowed

  // Hide rules
  hideSelectedActions?.fieldToHide?.forEach((rule) => {
    if (rule.hideValue.includes(rows[rule.field])) {
      rule.hideActions.forEach((action) => hidden.add(action));
    }
  });

  // Show rules (whitelist mode)
  hideSelectedActions?.fieldToShow?.forEach((rule) => {
    rule.showActions.forEach((action) => {
      if (rule.showValue.includes(rows[rule.field])) {
        whitelist.set(action, true); // allowed
      } else if (!whitelist.has(action)) {
        whitelist.set(action, false); // explicitly blocked if condition not matched
      }
    });
  });

  // Final visible actions
  return items.filter((action) => {
    // If action has whitelist rule → only show if true
    if (whitelist.has(action)) {
      return whitelist.get(action) === true;
    }

    // Else → apply hide rules
    return !hidden.has(action);
  });
};
const isAllActionsHidden = (): boolean => {
  return getVisibleActions().length === 0;
};

  return (
    <>
      {!isAllActionsHidden() && (
        <IconButton onClick={handleClick} className={`icon-button-${align}`} sx={{ height: 22, width: 22 }}>
          {align === 'horizontal' ? (
            <MoreHorizIcon />
          ) : (
            <MoreVertIcon sx={{ fontSize: '1.25rem' }} />
          )}
        </IconButton>
      )}
      <Menu
        style={{ marginLeft: 22 }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {items
          ?.filter((item) => {
            const showRules = hideSelectedActions?.fieldToShow ?? [];
            const hideRules = hideSelectedActions?.fieldToHide ?? [];

            // 1. If item is in showActions rules, show only if row matches showValue
            const showRule = showRules.find(rule => rule.showActions.includes(item));
            if (showRule) {
              return showRule.showValue.includes(rows?.[showRule.field]);
            }

            // 2. If item is in hideActions rules, hide if row matches hideValue
            const hideRule = hideRules.find(rule => rule.hideActions.includes(item));
            if (hideRule) {
              return !hideRule.hideValue.includes(rows?.[hideRule.field]);
            }

            // 3. Default: allow show if not hidden by checkFields
            return !checkFields(item);
          })
          .map((item) => (
            <MenuItem
              key={item}
              onClick={() => handleMenuItemClick(item)}
              sx={{ height: 30, fontSize: '0.75rem', minWidth: 70 }}
            >
              {item}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default ThreeDotMenu;
